import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import simpleBlockchainService from '../services/simpleBlockchainService';
import { useWallet } from '../context/SimpleWalletContext';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useFirebaseAuth();
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [solToUsdRate] = useState(190.67); // 1 SOL = $190.67 (average of $184.15-$197.19)
  const [isInvesting, setIsInvesting] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [solPrice, setSolPrice] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);

  const { connected, connect, balance, updateBalance, addInvestment } = useWallet();

  // Mock project data - updated to match main page projects
  const mockProject = {
    id: parseInt(id || '1'),
    name: id === '1' ? 'SOLAR_ENERGY' : id === '2' ? 'WIND_ENERGY' : 'ENERGY_STORAGE',
    description: id === '1'
      ? 'Advanced photovoltaic systems with 85% efficiency. This project involves installing state-of-the-art solar panels across multiple locations in Pakistan, providing clean energy to thousands of households.'
      : id === '2'
        ? 'Smart wind turbines with real-time optimization. This wind farm project utilizes cutting-edge turbine technology to harness wind energy efficiently.'
        : 'Advanced battery systems with 95% efficiency. This project focuses on developing large-scale energy storage solutions for renewable energy integration.',
    targetAmount: id === '1' ? 500000 : id === '2' ? 750000 : 1000000,
    raisedAmount: id === '1' ? 125000 : id === '2' ? 200000 : 300000,
    status: 'active',
    totalInvestors: id === '1' ? 45 : id === '2' ? 32 : 28,
    location: 'Pakistan',
    expectedROI: id === '1' ? '24.5%' : id === '2' ? '26.8%' : '28.2%',
    duration: '5 years',
  };

  const progressPercentage = Math.round((mockProject.raisedAmount / mockProject.targetAmount) * 100);
  const remainingAmount = mockProject.targetAmount - mockProject.raisedAmount;

  // Load wallet data and SOL price
  useEffect(() => {
    const loadWalletData = async () => {
      if (connected) {
        // Use the wallet context balance instead of service
        setWalletBalance(balance);
      }

      const price = await simpleBlockchainService.getSolPrice();
      setSolPrice(price);
    };

    loadWalletData();
  }, [connected, balance]);

  const handleInvestment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      alert('Please log in to make investments!');
      return;
    }

    if (!connected) {
      alert('Please connect your wallet first!');
      return;
    }

    setIsInvesting(true);

    try {
      const solAmount = parseFloat(investmentAmount);
      const usdAmount = solAmount * solToUsdRate; // Convert SOL to USD for display

      // Create payment with blockchain service
      const paymentResult = await simpleBlockchainService.sendPayment(null, {
        projectId: mockProject.id,
        amount: usdAmount,
        solAmount: solAmount,
        recipientAddress: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'
      });

      if (paymentResult.success) {
        // Get current user email for investment tracking
        const userEmail = localStorage.getItem('userEmail') || user.email || 'demo@example.com';

        // Update wallet balance in context
        updateBalance(balance - solAmount);

        // Record the investment in localStorage
        addInvestment({
          projectId: mockProject.id,
          projectName: mockProject.name,
          solAmount: solAmount,
          usdAmount: usdAmount,
          userEmail: userEmail
        });

        // Simulate successful investment without database storage
        setSuccessData({
          amount: usdAmount,
          solPaid: solAmount.toFixed(4),
          transaction: paymentResult.signature,
          projectName: mockProject.name
        });
        setShowSuccessModal(true);
        setInvestmentAmount('');

        // Update local wallet balance display
        setWalletBalance(balance - solAmount);
      } else {
        alert(`Payment failed: ${paymentResult.error}`);
      }
    } catch (error: any) {
      console.error('Investment error:', error);
      alert(`Investment failed: ${error.message}`);
    } finally {
      setIsInvesting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-6">
          <Link
            to="/projects"
            className="text-blue-600 hover:text-blue-500 text-sm font-medium"
          >
            ← Back to Projects
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                  {mockProject.name}
                </h1>

                <div className="mb-6">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Funding Progress</span>
                    <span>{progressPercentage}% Complete</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      ${mockProject.targetAmount.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">Target</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      ${mockProject.raisedAmount.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">Raised</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      ${remainingAmount.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">Remaining</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">
                      {mockProject.totalInvestors}
                    </div>
                    <div className="text-sm text-gray-600">Investors</div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Project Description
                    </h3>
                    <p className="text-gray-600">
                      {mockProject.description}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Project Details
                    </h3>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Location</dt>
                        <dd className="text-sm text-gray-900">{mockProject.location}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Expected ROI</dt>
                        <dd className="text-sm text-gray-900">{mockProject.expectedROI}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Duration</dt>
                        <dd className="text-sm text-gray-900">{mockProject.duration}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Status</dt>
                        <dd className="text-sm">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Recent Investments
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">Demo Investment</div>
                            <div className="text-xs text-gray-500">5 SOL • {new Date().toLocaleDateString()}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-green-600">$953.35</div>
                            <div className="text-xs text-gray-500">Completed</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">Sample Investment</div>
                            <div className="text-xs text-gray-500">2 SOL • {new Date(Date.now() - 86400000).toLocaleDateString()}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-green-600">$381.34</div>
                            <div className="text-xs text-gray-500">Completed</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">Early Investor</div>
                            <div className="text-xs text-gray-500">10 SOL • {new Date(Date.now() - 172800000).toLocaleDateString()}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-green-600">$1,906.70</div>
                            <div className="text-xs text-gray-500">Completed</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg sticky top-6">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Make an Investment
                </h3>

                {/* Wallet Info */}
                {connected ? (
                  <div className="bg-green-50 p-3 rounded-lg mb-4">
                    <div className="text-xs text-green-800">
                      <div className="font-semibold">Wallet Connected ✅</div>
                      <div>Balance: {balance.toFixed(4)} SOL</div>
                      <div>Rate: 1 SOL = $190.67</div>
                      {investmentAmount && (
                        <div className="mt-1 font-medium">
                          USD Value: ${(parseFloat(investmentAmount) * solToUsdRate).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 p-3 rounded-lg mb-4">
                    <div className="text-xs text-yellow-800">
                      <div className="font-semibold">⚠️ Connect Wallet Required</div>
                      <div>Connect your wallet to invest</div>
                    </div>
                    <button
                      onClick={connect}
                      className="mt-2 w-full bg-yellow-600 hover:bg-yellow-700 text-white text-xs py-2 px-3 rounded"
                    >
                      Connect Wallet
                    </button>
                  </div>
                )}

                <form onSubmit={handleInvestment} className="space-y-4">
                  <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                      Investment Amount (SOL)
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">SOL</span>
                      </div>
                      <input
                        type="number"
                        name="amount"
                        id="amount"
                        min="1"
                        step="0.1"
                        value={investmentAmount}
                        onChange={(e) => setInvestmentAmount(e.target.value)}
                        className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-12 pr-12 sm:text-sm border-gray-300 rounded-md"
                        placeholder="1.0"
                        required
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Minimum investment: 1 SOL (≈ $190.67)
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isInvesting || !investmentAmount || parseFloat(investmentAmount) < 1 || parseFloat(investmentAmount) > balance || !connected}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium py-3 px-4 rounded-md text-sm"
                  >
                    {isInvesting ? 'Processing...' : 'Invest Now'}
                  </button>
                </form>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    Why Invest?
                  </h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Support clean energy in Pakistan</li>
                    <li>• Competitive returns</li>
                    <li>• Verified projects</li>
                    <li>• Low minimum investment</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.95)' }}>
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowSuccessModal(false)}>
              <div className="absolute inset-0" style={{ background: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(8px)' }}></div>
            </div>

            <div className="max-w-md w-full relative z-10 p-8 rounded-2xl" style={{
              background: 'rgba(15, 23, 42, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(110, 255, 157, 0.8)',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.8), 0 0 40px rgba(110, 255, 157, 0.4)'
            }}>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full" style={{
                  background: 'linear-gradient(135deg, #6eff9d 0%, #10b981 100%)',
                  boxShadow: '0 0 20px rgba(110, 255, 157, 0.6)'
                }}>
                  <span className="text-3xl">🎉</span>
                </div>

                <h3 className="text-2xl font-bold mb-6 font-mono" style={{ color: '#ffffff', textShadow: '0 0 10px rgba(110, 255, 157, 0.5)' }}>
                  Successfully invested {successData?.solPaid} SOL in {successData?.projectName}!
                </h3>

                {successData && (
                  <div className="space-y-3 text-sm">
                    <div className="glass-card p-4 text-left" style={{
                      background: 'rgba(30, 41, 59, 0.8)',
                      border: '1px solid var(--border-subtle)'
                    }}>
                      <div className="flex justify-between items-center mb-2">
                        <span style={{ color: 'var(--secondary-text)' }}>Amount:</span>
                        <span className="font-mono font-bold" style={{ color: 'var(--neon-green)' }}>${successData.amount}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: 'var(--secondary-text)' }}>SOL paid:</span>
                        <span className="font-mono font-bold" style={{ color: 'var(--neon-green)' }}>{successData.solPaid}</span>
                      </div>
                    </div>

                    <div className="glass-card p-4" style={{
                      background: 'rgba(30, 41, 59, 0.8)',
                      border: '1px solid var(--border-subtle)'
                    }}>
                      <div className="text-xs font-mono mb-2" style={{ color: 'var(--dim-text)' }}>TRANSACTION_ID:</div>
                      <div className="font-mono text-xs break-all p-2 rounded" style={{
                        color: 'var(--cyber-blue)',
                        background: 'rgba(14, 165, 233, 0.1)',
                        border: '1px solid rgba(14, 165, 233, 0.2)'
                      }}>
                        {successData.transaction}
                      </div>
                    </div>

                    {successData.warning && (
                      <div className="p-3 rounded-lg" style={{
                        background: 'rgba(245, 158, 11, 0.15)',
                        border: '1px solid rgba(245, 158, 11, 0.4)'
                      }}>
                        <div className="text-xs font-mono" style={{ color: 'var(--warning)' }}>
                          ⚠️ {successData.warning}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="neon-btn px-8 py-3 mt-6 font-mono tracking-wide"
                >
                  CLOSE
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
